/**
 * Note telemetry service for tracking user interactions with notes
 * Provides batched event tracking for selection, copy, view, and section analytics
 * Events are batched and flushed periodically to reduce backend load
 */

import { encountersApi } from '@features/encounters/store/api';
import { trackNoteTelemetryToStatsig } from '@services/statsig/trackNoteTelemetryToStatsig';
import type { PostV2ProviderEncountersTelemetryApiResponse } from '@store/generated/encounters';
import { store } from '@store/index';

import { validateAndCoerceNoteEvent } from './noteTelemetrySchema';

export type SelectionMetrics = {
  selectionId: string;
  docId: string;
  docVersion: string;
  chars: {
    selected: number;
    total: number;
    percent: number;
  };
  timings: {
    startedAt: number;
    lastChangedAt: number;
    settledAt?: number;
    msToSettle?: number;
    copiedAt?: number;
    msToCopy?: number;
  };
  modality?: 'mouse' | 'touch' | 'keyboard' | 'copy_button' | 'copy_all' | 'copy_selected';
  isBackward?: boolean;
  ua?: string;
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
};

export type NoteTelemetryEventType =
  | 'selection_update'
  | 'text_selected'
  | 'text_copied'
  | 'note_viewed'
  | 'section_rendered'
  | 'section_interacted'
  | 'section_edited';

export type NoteViewedMetrics = {
  docId: string;
  docVersion: string;
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
};

export type SectionEventMetrics = {
  docId: string;
  docVersion: string;
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
  hoverDuration?: number; // Duration in milliseconds (for section_interacted events)
  interactionCounts?: {
    clicks?: number;
    selects?: number;
    copies?: number;
  };
};

export type EditEventMetrics = {
  docId: string;
  docVersion: string;
  edit: {
    charsBefore: number;
    charsAfter: number;
    charsDelta: number;
    percentDelta: number;
  };
  timings: {
    startedAt?: number;
    savedAt: number;
    msInEdit?: number;
  };
  source: 'manual' | 'ai_edit' | 'regeneration';
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
};

type QueuedEvent = {
  type: NoteTelemetryEventType;
  payload: SelectionMetrics | NoteViewedMetrics | SectionEventMetrics | EditEventMetrics;
};

/**
 * Service for batching and tracking note telemetry events
 * Buffers events and processes them in batches to reduce backend load
 *
 * Events are gated until encounter_id is set. All events are automatically
 * enriched with encounter_id at queue time and flush time.
 */
export class NoteTelemetryService {
  private static eventQueue: QueuedEvent[] = [];
  private static flushTimeout: NodeJS.Timeout | null = null;
  private static readonly FLUSH_INTERVAL = 5000; // 5 seconds
  private static readonly MAX_BATCH_SIZE = 50; // Max events per batch
  private static started = false;
  private static listenersAbortController: AbortController | null = null;
  // Dev-only instrumentation
  private static activeTimers = 0;

  // Global encounter_id context - events are gated until this is set
  private static encounterId: string | null = null;

  /**
   * Ensure the service is started exactly once.
   * Registers lifecycle listeners for page visibility/unload to flush and cleanup.
   */
  private static ensureStarted() {
    if (this.started) return;
    this.started = true;
    this.listenersAbortController = new AbortController();
    const { signal } = this.listenersAbortController;

    const handleBeforeUnload = () => {
      // Best-effort synchronous flush
      try {
        this.forceFlush();
      } catch {}
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        try {
          this.forceFlush();
        } catch {}
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload, { signal });
    document.addEventListener('visibilitychange', handleVisibilityChange, { signal });

    if (process.env.NODE_ENV !== 'production') {
      // Minimal dev log to detect leaks under StrictMode
      console.debug('[Note Telemetry] Service started');
    }
  }

  /**
   * Dispose lifecycle listeners and timers. Safe to call multiple times.
   */
  static dispose() {
    if (this.listenersAbortController) {
      this.listenersAbortController.abort();
      this.listenersAbortController = null;
    }
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }
    // Flush any remaining events
    void this.flushEvents();
    this.started = false;
  }

  /**
   * Set the encounter_id context. If switching to a new ID, flushes the current queue first.
   * This gates all future flushes until encounter_id is set.
   */
  static setEncounterId(encounterId: string): void {
    // If switching IDs, flush the current queue first
    if (
      this.encounterId !== null &&
      this.encounterId !== encounterId &&
      this.eventQueue.length > 0
    ) {
      this.forceFlush();
    }

    this.encounterId = encounterId;

    // If we have queued events and encounter_id is now set, schedule a flush
    if (this.encounterId && this.eventQueue.length > 0 && !this.flushTimeout) {
      this.flushTimeout = setTimeout(() => {
        void this.flushEvents();
      }, this.FLUSH_INTERVAL);
    }
  }

  /**
   * Enrich a payload with encounter_id from context if available
   */
  private static enrichWithContext(
    payload: SelectionMetrics | NoteViewedMetrics | SectionEventMetrics | EditEventMetrics,
  ): SelectionMetrics | NoteViewedMetrics | SectionEventMetrics | EditEventMetrics {
    if (!this.encounterId) return payload;

    // Create a new payload object to avoid mutating the original
    const enriched = { ...payload };

    if (!enriched.metadata) {
      enriched.metadata = {};
    }

    // Always set encounter_id from context if not already present
    if (!enriched.metadata.encounter_id) {
      enriched.metadata = {
        ...enriched.metadata,
        encounter_id: this.encounterId,
      };
    }

    return enriched;
  }

  /**
   * Queue an event for batched processing
   * Events are enriched with encounter_id at queue time (if available)
   */
  static queueEvent(
    type: NoteTelemetryEventType,
    payload: SelectionMetrics | NoteViewedMetrics | SectionEventMetrics | EditEventMetrics,
  ) {
    // Ensure lifecycle listeners are registered lazily
    this.ensureStarted();
    // Enrich with encounter_id at queue time
    const enrichedPayload = this.enrichWithContext(payload);
    this.eventQueue.push({ type, payload: enrichedPayload });

    // Only schedule flush if encounter_id is set
    if (!this.encounterId) {
      return; // Gate flush until encounter_id is available
    }

    // Set up flush if not already scheduled
    if (!this.flushTimeout) {
      this.activeTimers += 1;
      this.flushTimeout = setTimeout(() => {
        void this.flushEvents();
      }, this.FLUSH_INTERVAL);
    }

    // Immediately flush if we've reached max batch size
    if (this.eventQueue.length >= this.MAX_BATCH_SIZE) {
      if (this.flushTimeout) {
        clearTimeout(this.flushTimeout);
        this.flushTimeout = null;
      }
      this.flushEvents();
    }
  }

  /**
   * Flush all queued events and process them
   * Sends events to backend API and logs to console for debugging
   *
   * Events are gated until encounter_id is set. Events are enriched
   * with encounter_id again at flush time for safety.
   */
  static async flushEvents() {
    // Gate flush until encounter_id is available
    if (!this.encounterId) {
      return;
    }

    if (this.eventQueue.length === 0) return;

    // Enrich events again at flush time for safety (in case encounter_id was set after queueing)
    const events = this.eventQueue.map(({ type, payload }) => ({
      type,
      payload: this.enrichWithContext(payload),
    }));

    this.eventQueue = [];
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
      if (process.env.NODE_ENV !== 'production') {
        this.activeTimers = Math.max(0, this.activeTimers - 1);
      }
    }

    // Extract note_id from events metadata (use first available, they should all be the same)
    const noteId =
      events.length > 0 && events[0].payload.metadata?.note_id
        ? events[0].payload.metadata.note_id
        : null;

    // Transform events for API format
    const apiEvents = events.map(({ type, payload }) => ({
      type,
      ...payload,
    }));

    // Send to backend API
    try {
      // Type assertion needed because dispatch(initiate()) return type is complex
       
      const result = (await store.dispatch(
        encountersApi.endpoints.postV2ProviderEncountersTelemetry.initiate({
          logTelemetryEventsRequest: {
            encounter_id: this.encounterId,
            events: apiEvents,
            note_id: noteId || null,
          },
        }),
      )) as { data?: PostV2ProviderEncountersTelemetryApiResponse; error?: unknown };

      // Log batch summary header
      console.groupCollapsed(`[Note Telemetry] Batch Flush: ${events.length} events`);
      console.log(`📦 Flushed ${events.length} events at ${new Date().toISOString()}`);
      if (result?.data) {
        console.log(`✅ Backend response:`, result.data);
      } else if (result?.error) {
        console.error(`❌ Backend error:`, result.error);
      }

      // Also log each event for debugging
      events.forEach(({ type, payload }) => {
        this.logEventImmediate(type, payload);
      });

      console.groupEnd();
    } catch (error) {
      // Log error but don't throw - telemetry failures shouldn't break the app
      console.error('[Note Telemetry] Failed to send events to backend:', error);

      // Still log events to console for debugging
      console.groupCollapsed(
        `[Note Telemetry] Batch Flush (Console Only): ${events.length} events`,
      );
      events.forEach(({ type, payload }) => {
        this.logEventImmediate(type, payload);
      });
      console.groupEnd();
    }
  }

  /**
   * Force flush all queued events immediately
   * Useful when component unmounts or page is closing
   */
  static forceFlush() {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }
    this.flushEvents();
  }

  /**
   * Log an event immediately (internal method used during flush)
   * TODO: Replace with backend API call when ready
   */
  private static logEventImmediate(
    type: NoteTelemetryEventType,
    payload: SelectionMetrics | NoteViewedMetrics | SectionEventMetrics | EditEventMetrics,
  ): void {
    // Validate and coerce payload before logging
    const validatedPayload = validateAndCoerceNoteEvent(type, payload);
    // Handle note_viewed, section_rendered, section_interacted, and section_edited events separately (simpler payload)
    if (
      type === 'note_viewed' ||
      type === 'section_rendered' ||
      type === 'section_interacted' ||
      type === 'section_edited'
    ) {
      const eventPayload = validatedPayload as
        | NoteViewedMetrics
        | SectionEventMetrics
        | EditEventMetrics;
      console.groupCollapsed(`[Note Telemetry] ${type}`, eventPayload.docId);

      // Document info
      console.log('📄 Document:', eventPayload.docId, `v${eventPayload.docVersion}`);

      // Metadata
      if (eventPayload.metadata) {
        const metadata: Record<string, string | undefined> = {};
        if (eventPayload.metadata.module_id)
          metadata['Module ID'] = eventPayload.metadata.module_id;
        if (eventPayload.metadata.section_id)
          metadata['Section ID'] = eventPayload.metadata.section_id;
        if (eventPayload.metadata.note_id) metadata['Note ID'] = eventPayload.metadata.note_id;
        if (eventPayload.metadata.section_title)
          metadata['Section Title'] = eventPayload.metadata.section_title;
        if (eventPayload.metadata.encounter_id)
          metadata['Encounter ID'] = eventPayload.metadata.encounter_id;
        if (Object.keys(metadata).length > 0) {
          console.table(metadata);
        }
      }

      // Interaction duration and counts (for section_interacted events)
      if (type === 'section_interacted') {
        const sectionPayload = eventPayload as SectionEventMetrics;
        const interactionData: Record<string, string> = {};
        if (sectionPayload.hoverDuration !== undefined) {
          interactionData['Hover Duration'] = sectionPayload.hoverDuration.toFixed(2);
        }
        if (sectionPayload.interactionCounts) {
          if (sectionPayload.interactionCounts.clicks !== undefined) {
            interactionData['Clicks'] = sectionPayload.interactionCounts.clicks.toString();
          }
          if (sectionPayload.interactionCounts.selects !== undefined) {
            interactionData['Selects'] = sectionPayload.interactionCounts.selects.toString();
          }
          if (sectionPayload.interactionCounts.copies !== undefined) {
            interactionData['Copies'] = sectionPayload.interactionCounts.copies.toString();
          }
        }
        if (Object.keys(interactionData).length > 0) {
          console.table(interactionData);
        }
      }

      // Edit metrics (for section_edited events)
      if (type === 'section_edited') {
        const editPayload = eventPayload as EditEventMetrics;
        const editData: Record<string, string> = {
          Before: `${editPayload.edit.charsBefore.toLocaleString()} chars`,
          After: `${editPayload.edit.charsAfter.toLocaleString()} chars`,
          Delta: `${editPayload.edit.charsDelta >= 0 ? '+' : ''}${editPayload.edit.charsDelta.toLocaleString()} chars`,
          Percent: `${(editPayload.edit.percentDelta * 100).toFixed(2)}%`,
          Source: editPayload.source,
        };
        console.table(editData);

        const timingData: Record<string, string> = {
          'Saved At': editPayload.timings.savedAt.toFixed(2),
        };
        if (editPayload.timings.startedAt !== undefined) {
          timingData['Started At'] = editPayload.timings.startedAt.toFixed(2);
        }
        if (editPayload.timings.msInEdit !== undefined) {
          timingData['Time in Edit'] = editPayload.timings.msInEdit.toFixed(2);
        }
        console.table(timingData);
      }

      // Full payload available for inspection
      console.log('📦 Full Payload:', eventPayload);
      console.groupEnd();
      return;
    }

    // Handle selection/copy events (existing logic)
    const selectionPayload = validatedPayload as SelectionMetrics;
    // Use collapsed group for cleaner console output
    console.groupCollapsed(`[Note Telemetry] ${type}`, selectionPayload.selectionId.slice(0, 8));

    // Document info
    console.log('📄 Document:', selectionPayload.docId, `v${selectionPayload.docVersion}`);

    // Character metrics as table (guard against invalid percent)
    const percentValue =
      isNaN(selectionPayload.chars.percent) ||
      selectionPayload.chars.percent < 0 ||
      selectionPayload.chars.percent > 1
        ? 0
        : selectionPayload.chars.percent;
    console.table({
      Selected: `${selectionPayload.chars.selected.toLocaleString()} chars`,
      Total: `${selectionPayload.chars.total.toLocaleString()} chars`,
      Percent: `${(percentValue * 100).toFixed(2)}%`,
    });

    // Timing metrics as table
    const timingData: Record<string, string> = {
      'Started At': selectionPayload.timings.startedAt.toFixed(2),
      'Last Changed': selectionPayload.timings.lastChangedAt.toFixed(2),
    };

    if (selectionPayload.timings.settledAt !== undefined) {
      timingData['Settled At'] = selectionPayload.timings.settledAt.toFixed(2);
    }
    if (selectionPayload.timings.msToSettle !== undefined) {
      timingData['Time to Settle'] = selectionPayload.timings.msToSettle.toFixed(2);
    }
    if (selectionPayload.timings.copiedAt !== undefined) {
      timingData['Copied At'] = selectionPayload.timings.copiedAt.toFixed(2);
    }
    if (selectionPayload.timings.msToCopy !== undefined) {
      timingData['Time to Copy'] = selectionPayload.timings.msToCopy.toFixed(2);
    }

    console.table(timingData);

    // Additional metadata
    const metadata: Record<string, string | boolean | undefined> = {
      Modality: selectionPayload.modality || 'unknown',
      'Is Backward': selectionPayload.isBackward ?? false,
    };

    // Don't log hashedSelected (deprecated/removed)

    if (selectionPayload.metadata) {
      if (selectionPayload.metadata.module_id)
        metadata['Module ID'] = selectionPayload.metadata.module_id;
      if (selectionPayload.metadata.section_id)
        metadata['Section ID'] = selectionPayload.metadata.section_id;
      if (selectionPayload.metadata.note_id)
        metadata['Note ID'] = selectionPayload.metadata.note_id;
      if (selectionPayload.metadata.section_title)
        metadata['Section Title'] = selectionPayload.metadata.section_title;
      if (selectionPayload.metadata.encounter_id)
        metadata['Encounter ID'] = selectionPayload.metadata.encounter_id;
    }

    console.table(metadata);

    // Full payload available for inspection
    console.log('📦 Full Payload:', selectionPayload);

    console.groupEnd();
  }
}

/**
 * Track a note telemetry event
 * Events are batched and processed periodically to reduce backend load
 * Also mirrors events to Statsig for dual tracking
 * @param type Event type (text_selected, text_copied, cut, note_viewed, section_rendered, section_interacted)
 * @param payload Selection metrics payload, note viewed payload, or section event payload
 */
export const trackNoteTelemetry = (
  type: NoteTelemetryEventType,
  payload: SelectionMetrics | NoteViewedMetrics | SectionEventMetrics | EditEventMetrics,
): void => {
  // Queue the event for batched processing (existing NoteTelemetry system)
  NoteTelemetryService.queueEvent(type, payload);

  // Also track to Statsig immediately (dual tracking)
  // This call is wrapped in try-catch internally, so it won't affect NoteTelemetry
  trackNoteTelemetryToStatsig(type, payload);
};

/**
 * Force flush all queued note telemetry events immediately
 * Useful for cleanup or testing
 */
export const flushNoteTelemetry = () => {
  NoteTelemetryService.forceFlush();
};

// Export types for backwards compatibility during migration
export type BlockNoteEventType = NoteTelemetryEventType;
